import { FilterName, FilterState } from "../../../state/AppState.ts";
import { HttpClient, Result } from "../../../../http/HttpClient.ts";
import { FilterPayloadMapper } from "./mappers/FilterPayloadMapper.ts";
import { FilterResponseMapper } from "./mappers/FilterResponseMapper.ts";

export interface FilterApiServiceConfig {
  enableDynamicFilters: boolean;
}

/**
 * Represents the return value from the filter options endpoint.
 *
 * In order to better represent the different filter options, a new format was introduced.
 */
export type FilterOptionsEndpointReturn = {
  /**
   * Legacy filter options format
   * @deprecated on August 2025
   */
  schema?: Record<
    string,
    {
      enabled: boolean;
      value: string[];
    }
  >;
  /**
   * New filter options
   * valid from August 2025
   */
  filters?: {
    /**
     * The filter label to be displayed to the user
     */
    label: string;
    /**
     * The filter name in the system
     */
    key: string;
    /**
     * The type of input to be displayed to the user
     */
    type: "selection_multiple" | "month_range";
    /**
     * Set to `true` if this is a custom filter for that specific Knowledge Source.
     * Set to `false` when this is a default filter from the system.
     *
     * Custom filters should be displayed on top of the other filters.
     */
    custom: boolean;
    /**
     * The options to be displayed to the user.
     * This is **required** for `selection_multiple` type filters.
     * If the type is `month_range`, this field is not used.
     */
    options?: {
      label: string;
      value: string;
    }[];
  }[];
};

export type FilterOptions = {
  /**
   * Legacy filter options format
   * @deprecated on August 2025
   */
  filterOptions?: Record<
    FilterName,
    {
      enabled: boolean;
      value: string[];
    }
  >;
  /**
   * New filter options
   * valid from August 2025
   */
  filterOptionsList?: FilterOptionsEndpointReturn["filters"];
};

/**
 * refactoring the FilterApiService to be a separate class with
 * the following methods extracted from the HttpApiService:
 * - getFilters
 * - setFilters
 * - getFilterOptions
 */
export class FilterApiService {
  private readonly filterPayloadMapper: FilterPayloadMapper;
  private readonly filterResponseMapper: FilterResponseMapper;
  private readonly config: FilterApiServiceConfig;

  constructor(
    private httpClient: HttpClient,
    config: Partial<FilterApiServiceConfig> = {},
  ) {
    this.filterPayloadMapper = new FilterPayloadMapper();
    this.filterResponseMapper = new FilterResponseMapper();
    this.config = {
      enableDynamicFilters: false,
      ...config,
    };
  }

  async getFilterOptions(payload: {
    researchId: string;
  }): Result<FilterOptions> {
    const endpoint = this.config.enableDynamicFilters
      ? `/research/${payload.researchId}/filters`
      : `/research/${payload.researchId}/filter-options`;

    const result =
      await this.httpClient.get<FilterOptionsEndpointReturn>(endpoint);
    const data: FilterOptions | undefined = result.data?.filters
      ? {
          filterOptionsList: result.data.filters,
        }
      : result.data?.schema
        ? {
            filterOptions: result.data.schema,
          }
        : undefined;
    return {
      success: result.success,
      data: data,
      httpStatus: result.httpStatus,
    };
  }

  async getFilters(payload: { researchId: string }): Result<FilterState> {
    const result = await this.httpClient.get<
      Partial<
        Omit<Record<FilterName, string[] | null>, "creation_date"> & {
          creation_date: {
            start_date: string;
            end_date: string;
          } | null;
        }
      >
    >(`/research/${payload.researchId}/filter/`);

    const filters = this.filterResponseMapper.apiToFrontend(result.data || {});

    return {
      success: result.success,
      data: filters,
      httpStatus: result.httpStatus,
    };
  }

  async setFilters(payload: {
    researchId: string;
    filters: Partial<FilterState>;
  }): Result {
    const filters = this.filterPayloadMapper.frontendToApi(payload);
    const result = await this.httpClient.post<{ id: string }>(
      `/research/${payload.researchId}/set-filter/`,
      filters,
    );
    return {
      success: result.success,
      httpStatus: result.httpStatus,
    };
  }
}
